/**
 * liveBoard.js — the Live Board's data layer: storage, the reload whitelist,
 * and the resolvers the entry row types against.
 *
 * Pure. No React, no engine, and deliberately NOTHING from useMockDraft — the
 * Live Board runs during a real auction with eleven people watching, so it
 * shares no mutable state with the mock draft and the freeze class of bug is
 * structurally unable to reach it.
 *
 * THE DATA MODEL. One array of sales, each carrying the team that won it:
 *
 *   { leagueName, startedAt, budget, teams, rosterSize, positions, userTeam,
 *     shareId, nomination, nominatorIndex, nomClockSec, draftAt, teamNames: [...],
 *     sales: [{ name, pos, posRank, price, team, ts }] }
 *
 * `team` is an index into `teamNames`. The user's own seat is just one of them
 * — there is no "me" flag, because a boolean cannot rebuild twelve rosters and
 * a team index gives the personal view for free by filtering.
 *
 * WHY posRank IS STORED ON THE SALE rather than looked up at render: the board
 * moves. A record written on August 22 should still price against the board it
 * was written on, not against whatever ADP ships in November.
 */
import { UNIQUE_PLAYERS } from '../data/players';
import { TEAM_NAMES } from '../data/archetypes';
import { getAdpRank, getEffectivePosRank } from '../data/adpData';
import { normName } from './playerNameNorm';
import { buildRosterSlots, fillRosterSlots } from './rosterSlotFill';
import { tierForPlayer } from './roomTemp';

export const LIVE_BOARD_KEY_PREFIX = 'auctionArchitect_liveBoard_';

// Matches the existing per-league convention (auctionArchitect_teamConfigs_,
// userTierSheet_): the "default" fallback keeps an unnamed board addressable
// instead of writing to a key ending in "undefined".
export function liveBoardKey(leagueName) {
  return LIVE_BOARD_KEY_PREFIX + (leagueName || 'default');
}

// ── The reload boundary ─────────────────────────────────────────────
// 🔴 TRAP: THIS IS A WHITELIST, AND A WHITELIST IS HOW YOU LOSE A FEATURE.
// sanitizeTeamConfigs (useMockDraft.js:51) rebuilds each seat field by field,
// which silently ate a `keepers` key on reload — it worked all session and was
// gone after a refresh, the failure mode that looks like the feature working.
// It has now bitten twice.
//
// So: `sales` and `teamNames` are named here explicitly, and
// liveBoard.test.js fails if either one stops round-tripping. If you add a
// field to the board, add it BOTH here and to that test, or it will not
// survive the refresh that this whole surface exists to survive.

function toInt(v, fallback) {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? n : fallback;
}

/**
 * One sale, validated. Returns null for anything that is not a usable record —
 * dropped rather than repaired, because a half-parsed sale would quietly
 * corrupt every roster and budget derived from it.
 */
export function sanitizeSale(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  const pos = typeof raw.pos === 'string' ? raw.pos.trim() : '';
  if (!name || !pos) return null;
  const price = toInt(raw.price, NaN);
  if (!Number.isFinite(price) || price < 0) return null;
  const team = toInt(raw.team, NaN);
  if (!Number.isFinite(team) || team < 0) return null;
  return {
    name,
    pos,
    posRank: typeof raw.posRank === 'string' ? raw.posRank : null,
    price,
    team,
    ts: Number.isFinite(Number(raw.ts)) ? Number(raw.ts) : 0,
  };
}

// The slot layout a live board rosters against.
//
// 🔴 WHY THE BOARD CARRIES `positions` AND NOT JUST A ROSTER-SIZE NUMBER.
// TeamGrid, ViewAllRostersModal and fillRosterSlots all need SLOT DEFINITIONS
// (QB, RB1, RB2, FLEX, BN3…), which only buildRosterSlots(positions) produces.
// If the slots came from app settings while openSlots came from a separately
// typed rosterSize, the two would disagree the moment they differ — the same
// screen would show "12 slots left" in the room read and a 16-slot roster grid,
// with max bid computed off whichever one got there first. Deriving both from
// one `positions` object makes that class of drift impossible.
const POS_KEYS = ['QB', 'RB', 'WR', 'TE', 'FLEX', 'SFLEX', 'DEF', 'K', 'BN'];

/** Take the league's starters and flex the BENCH so the total is `rosterSize`. */
export function positionsForRosterSize(base, rosterSize) {
  const out = {};
  for (const k of POS_KEYS) out[k] = Math.max(0, toInt(base?.[k], 0));
  const nonBench = POS_KEYS.filter(k => k !== 'BN').reduce((t, k) => t + out[k], 0);
  out.BN = Math.max(0, toInt(rosterSize, 16) - nonBench);
  return out;
}

export function sanitizePositions(raw, rosterSizeHint) {
  const has = raw && typeof raw === 'object' && POS_KEYS.some(k => toInt(raw[k], 0) > 0);
  if (has) {
    const out = {};
    for (const k of POS_KEYS) out[k] = Math.max(0, toInt(raw[k], 0));
    return out;
  }
  // Pre-chunk-3 boards stored only a number. Rebuild a sane layout around it so
  // an in-progress board from before this shipped still renders.
  return positionsForRosterSize({ QB: 1, RB: 2, WR: 3, TE: 1, FLEX: 1, DEF: 1, K: 1 }, rosterSizeHint);
}

export function rosterSizeOfPositions(positions) {
  return POS_KEYS.reduce((t, k) => t + Math.max(0, toInt(positions?.[k], 0)), 0);
}

/**
 * When the current nominator's clock started: the moment the last sale landed,
 * or the moment the board opened if nothing has sold yet.
 *
 * DERIVED FROM PERSISTED STATE on purpose. The old clock captured Date.now() at
 * mount, so it restarted on every remount — invisible while only the operator
 * saw it, and wrong the moment a spectator's page re-rendered. Both screens now
 * compute the same number from the same timestamps.
 */
export function waitingSince(board) {
  const sales = board?.sales || [];
  if (sales.length) return sales[sales.length - 1].ts || 0;
  return board?.startedAt || 0;
}

/**
 * Seconds left for the current nominator, or null when there is no clock to
 * show — off, nobody waiting (someone is already up), nothing sold yet, or no
 * start point.
 *
 * 🔴 BEFORE PICK ONE THE CLOCK DOES NOT EXIST, IT DOES NOT COUNT FROM
 * SOMETHING ELSE. `waitingSince` falls back to `startedAt`, so a board set up
 * at 6:00 for a 6:30 draft showed a full-size red 0:00 on the TV for half an
 * hour while the room sat down. Nobody is late before the draft starts, so
 * there is no wait to time. The `waitingSince` fallback stays — it has other
 * callers.
 *
 * ⚠️ The thing that SHOULD be on screen then already is: `DraftCountdown`
 * owns this spot before pick one. Two countdowns in one panel is the bug, not
 * the fix — do not reach for `draftAt` here.
 *
 * ⚠️ A red 0:00 AFTER a sale is correct and stays. The nominator genuinely is
 * over time. Only the no-sales-yet case changed.
 *
 * 🔴 DISPLAY ONLY. Floors at zero and stops. Nothing in the app reads this
 * value to decide anything.
 */
export function nominationSecondsLeft(board, nowMs) {
  const total = board?.nomClockSec || 0;
  if (!total || board?.nomination) return null;
  if (!(board?.sales || []).length) return null;
  const since = waitingSince(board);
  if (!since) return null;
  return Math.max(0, total - Math.floor((nowMs - since) / 1000));
}

/**
 * The player currently on the block, validated.
 *
 * Deliberately carries NO price and NO team: those are what the sale adds, and
 * a nomination that could carry them would be a sale in waiting. Returns null
 * for anything unusable, so a corrupt record clears the block rather than
 * wedging it.
 */
/**
 * A browser `datetime-local` value -> an absolute instant, or null.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE VALUE ARRIVING HERE IS NAIVE AND THAT IS THE ENTIRE PROBLEM.
 * `<input type="datetime-local">` hands over "2026-08-22T19:00" — a wall clock
 * with no zone attached. Store that string and every device renders its own
 * 7pm: eleven people in three zones each see a different instant, all of them
 * labelled correctly, and nobody finds out until somebody is an hour late.
 *
 * `new Date(s)` on a date-TIME form with no offset is defined to mean local
 * time, which is the right reading of what the person typed: 7pm where they
 * are. Calling `.getTime()` on it collapses that to an instant, and an instant
 * is the same number everywhere on earth.
 *
 * ⚠️ Do not "simplify" this to Date.parse of a date-only string — the spec
 * reads THOSE as UTC, so the same code would be right for one input format and
 * silently wrong for the other.
 * ══════════════════════════════════════════════════════════════════════
 */
export function epochFromLocalInput(value) {
  if (typeof value !== 'string' || !value.trim()) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * A browser `date` value ("2026-08-29") -> an absolute instant, or null.
 *
 * The sibling of `epochFromLocalInput`, and it lives here rather than next to
 * its caller precisely BECAUSE of the warning above: `new Date("2026-08-29")`
 * is defined to mean UTC midnight, so the obvious one-liner is silently wrong
 * for anyone west of Greenwich. Someone who wanted a date-only version and did
 * not find one here would write that one-liner. Now they find this.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE INSTANT IS LOCAL NOON, AND THAT IS NOT AN ARBITRARY CHOICE.
 * A calendar date is not an instant, so turning one into an instant has to pick
 * a time, and every consumer downstream reads a calendar date back OUT of it in
 * some other zone. `api/email/trends-signup.js` converts to US Eastern.
 *
 * Local MIDNIGHT loses a day for every user east of Eastern — including all of
 * Europe and even UTC itself (midnight UTC Aug 29 is 8pm Aug 28 in New York).
 * Local NOON is the standard fix: it puts ~12 hours of slack on both sides, so
 * the date survives the round trip for every zone from UTC-11 to UTC+8. That
 * covers every US timezone with hours to spare, which is this app's audience.
 *
 * ⚠️ KNOWN RESIDUAL: UTC+9 and east (Japan, Korea, most of Australia) still
 * round-trip one day EARLY through an Eastern conversion — noon in Tokyo is the
 * previous evening in New York. The real fix is to send the date STRING and
 * never build an instant at all; that is an endpoint contract change, not a
 * helper change. Do not "fix" it here by shifting the hour — every hour you pick
 * breaks a different set of zones.
 * ══════════════════════════════════════════════════════════════════════
 *
 * Junk is null, never a wrong date: the format is matched strictly, and a
 * rolled-over date ("2026-02-31" — which the Date constructor happily turns
 * into March 3rd) is rejected by reading the fields back.
 */
export function epochFromLocalDate(value) {
  if (typeof value !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(year, month - 1, day, 12, 0, 0, 0);
  // Round-trip check: catches month 13, day 31 of February, and day 0 in one
  // test, without a calendar table.
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return null;
  const ms = dt.getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * What the countdown should say right now, or null for "say nothing".
 *
 * 🔴 THE FIRST SALE ENDS IT, and that outranks the clock. A draft that starts
 * early is normal; a sale is what actually happened, the schedule was only a
 * plan, and nobody wants a timer in the corner for the next three hours.
 *
 * 🔴 THE WINDOW AFTER THE TIME AND BEFORE PICK ONE READS "NOW". Counting up is
 * noise and a negative number looks like a bug to everyone who sees it.
 *
 * Pure, and takes `now` as an argument — the ticker lives in the display
 * component so this file stays timer-free, which a test enforces.
 */
export function draftCountdown(board, now) {
  const at = board?.draftAt;
  if (!Number.isFinite(at) || at <= 0) return null;
  if (board?.sales?.length) return null;              // under way: the plan is history
  const ms = at - now;
  if (ms <= 0) return { state: 'now', at, ms: 0 };
  const s = Math.floor(ms / 1000);
  return {
    state: 'pending',
    at,
    ms,
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

export function sanitizeNomination(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const name = typeof raw.name === 'string' ? raw.name.trim() : '';
  const pos = typeof raw.pos === 'string' ? raw.pos.trim() : '';
  if (!name || !pos) return null;
  return {
    name,
    pos,
    // THIS is the whitelist for the block. `team` and `sleeper_id` are what the
    // photo resolves from, so a nomination that survives a reload without them
    // comes back as an initials circle — working, but visibly not the same
    // screen the room was looking at a second ago.
    team: typeof raw.team === 'string' ? raw.team : null,
    sleeper_id: raw.sleeper_id != null ? String(raw.sleeper_id) : null,
    // posRank is deliberately NOT carried. It is a ranking, and the block shows
    // no ranking, value or number — see NominationBlock.
    ts: Number.isFinite(Number(raw.ts)) ? Number(raw.ts) : 0,
  };
}

/** A whole board, validated. Returns null when there is nothing usable. */
export function sanitizeLiveBoard(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const teamNames = Array.isArray(raw.teamNames)
    ? raw.teamNames.map(n => (typeof n === 'string' ? n : '')).filter(n => n.length > 0)
    : [];
  if (!teamNames.length) return null;             // a board with no teams cannot be entered against
  const sales = Array.isArray(raw.sales)
    ? raw.sales.map(sanitizeSale).filter(Boolean).filter(s => s.team < teamNames.length)
    : [];
  const positions = sanitizePositions(raw.positions, raw.rosterSize);
  // userTeam: which seat is the user's. -1 is legitimate ("not saying"), so it
  // is stored rather than defaulted to 0, which would silently label someone
  // else's team as his.
  const rawUser = toInt(raw.userTeam, -1);
  const rawNom = toInt(raw.nominatorIndex, 0);
  return {
    leagueName: typeof raw.leagueName === 'string' ? raw.leagueName : '',
    // 🔴 WHITELISTED. The spectator feed is addressed by this id and nothing
    // else — lose it on reload and every viewer's link goes dead mid-draft
    // while the board on screen looks perfectly fine. That is the silent
    // failure this whitelist has produced twice before.
    shareId: typeof raw.shareId === 'string' ? raw.shareId : null,
    // 🔴 WHITELISTED, AND THE FOURTH KEY THIS LIST HAS HAD TO LEARN.
    // A refresh mid-nomination must come back with the player still on the
    // block — the room is looking at him. Note this is deliberately NOT part of
    // `sales`: a nomination is not a sale, so nothing derived from sales
    // (budgets, rosters, max bid, needs, roomTemp, the cheat sheet's gone
    // state) can see it, and a player on the block is still available.
    nomination: sanitizeNomination(raw.nomination),
    // Whose turn it is to nominate. ADVISORY — a prediction the user can
    // correct, never a gate. Persisted so a refresh does not reset the guess to
    // seat 0 mid-draft.
    nominatorIndex: rawNom >= 0 && rawNom < teamNames.length ? rawNom : 0,
    // How long the nomination clock runs, in seconds. 0 = off.
    //
    // 🔴 THE DEFAULT IS 60, AND IT IS A DEFAULT FOR BOARDS THAT NEVER HAD A
    // VALUE — never an override for one that does. `toInt` returns the stored
    // number whenever it is finite, and 0 is finite: a board somebody turned
    // the clock OFF on stays off through every reload and every publish. Only
    // a record with the key missing or corrupt lands on 60.
    //
    // It defaults ON because the thing it fixes is a room, not a screen. A
    // nominator who has gone quiet is the single most common stall in a live
    // auction, and a clock nobody switched on cannot end one. An operator who
    // does not want it turns it off in one click; an operator who never found
    // the setting sat through it.
    //
    // 🔴 IT LIVES IN BOARD STATE, NOT IN THE COMPONENT, because /watch has to
    // show the same clock as the operator. A duration held in local component
    // state is unpublishable, so every spectator would either see no clock or
    // their own — and a countdown that disagrees between the two screens in a
    // room reading both is worse than none.
    nomClockSec: Math.max(0, toInt(raw.nomClockSec, 60)),
    // 🔴 WHITELISTED, AND THE FIFTH KEY THIS LIST HAS HAD TO LEARN. When the
    // draft starts, as an ABSOLUTE INSTANT — epoch ms, never the naive
    // "2026-08-22T19:00" the input produces (see epochFromLocalInput). It is
    // published, so eleven devices render this one number in eleven local
    // zones; store a wall clock instead and they would each show their own.
    //
    // Optional by design: null is the normal state and means the countdown
    // does not exist anywhere. A non-number coerces to null rather than to a
    // wrong time — a countdown to the wrong hour is worse than no countdown.
    draftAt: Number.isFinite(Number(raw.draftAt)) && Number(raw.draftAt) > 0
      ? Number(raw.draftAt)
      : null,
    startedAt: Number.isFinite(Number(raw.startedAt)) ? Number(raw.startedAt) : 0,
    budget: Math.max(1, toInt(raw.budget, 200)),
    teams: Math.max(1, toInt(raw.teams, teamNames.length)),
    rosterSize: rosterSizeOfPositions(positions),
    positions,
    userTeam: rawUser >= 0 && rawUser < teamNames.length ? rawUser : -1,
    teamNames,
    sales,
  };
}

export function readLiveBoard(leagueName) {
  try {
    return sanitizeLiveBoard(JSON.parse(localStorage.getItem(liveBoardKey(leagueName))));
  } catch {
    return null;
  }
}

/**
 * SYNCHRONOUS. Called inside the commit handler itself, never from an effect,
 * never debounced, never on an interval — ninety minutes in, the laptop sleeps
 * or someone refreshes, and a board that comes back empty is worse than the
 * spreadsheet it replaced.
 *
 * Returns false if the write failed (quota, private mode) so the caller can
 * say so out loud instead of pretending it saved.
 */
export function writeLiveBoard(board) {
  try {
    localStorage.setItem(liveBoardKey(board?.leagueName), JSON.stringify(board));
    return true;
  } catch {
    return false;
  }
}

export function clearLiveBoard(leagueName) {
  try {
    localStorage.removeItem(liveBoardKey(leagueName));
    return true;
  } catch {
    return false;
  }
}

// ── UNDO / REDO HISTORY ─────────────────────────────────────────────
/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 A STACK OF BOARD STATES, NOT A STACK OF OPERATIONS.
 *
 * Ben: "If we undo twice, it should undo the last two player sales and move the
 * nomination back two spots accordingly. So basically Undo/Redo."
 *
 * The inverse of an edit here is fiddly to derive and easy to get subtly wrong
 * — the aug-chunk-F correction, which had to record what the write did because
 * the obvious reverse condition fired in the wrong cases, is the proof. A
 * snapshot is exact BY CONSTRUCTION, at any depth, for every verb, forever.
 * The board is small enough that this costs nothing.
 *
 * 🔴 ITS OWN KEY, NEVER ON THE BOARD OBJECT. Two reasons and both are silent
 * failures: `sanitizeLiveBoard` is a rebuild-from-whitelist, so a history field
 * on the board would be dropped on the next reload with nothing to show for it;
 * and `publishBoard` ships the whole board to Upstash on every pick, so every
 * spectator would be downloading the operator's undo stack.
 *
 * ⚠️ THE KEY DOES NOT COLLIDE with LIVE_BOARD_KEY_PREFIX — same argument the
 * teams key already relies on. `auctionArchitect_liveBoardHistory_` diverges
 * from `auctionArchitect_liveBoard_` at the character after "liveBoard", so
 * `startsWith` is already false and otherBoardsInProgress cannot see these.
 * ══════════════════════════════════════════════════════════════════════
 */
export const LIVE_BOARD_HISTORY_PREFIX = 'auctionArchitect_liveBoardHistory_';

export function liveBoardHistoryKey(leagueName) {
  return LIVE_BOARD_HISTORY_PREFIX + (leagueName || 'default');
}

/**
 * How deep undo goes.
 *
 * ⚠️ RAISED FROM 10 WHEN THE DIALS CAME OUT OF THE STACK (chunk I). At 10, with
 * every commit recording, ten clicks of the advisory step arrows evicted three
 * real picks — see the note on `record` in useLiveBoard.commitBoard. Twenty
 * entries is now twenty REAL actions, roughly ten picks, which is far past what
 * anyone reaches at a live auction.
 *
 * 🔴 THE CAP IS A SIZE BUDGET, NOT A UX ONE. A snapshot of a full 160-pick board
 * measured ~14 KB, so twenty is ~280 KB in one localStorage key. Comfortable,
 * not free — this is the first number to cut if `storageFailed` ever fires on
 * draft night.
 */
export const HISTORY_DEPTH = 20;

/**
 * The three fields an undo has to restore, and deliberately no more.
 *
 * Everything else on a board is either FIXED for the whole draft (leagueName,
 * budget, teams, rosterSize, positions, shareId, startedAt) or per-device and
 * not an editing act (nomClockSec, userTeam) — snapshotting them would let an
 * undo of a sale quietly revert a setting somebody changed since.
 *
 * ⚠️ teamNames is NOT here, so undoing a rename does not restore the old name.
 * That is a known, accepted gap rather than an oversight: a rename is the one
 * verb whose old value is also written to the CROSS-SEASON saved-names key, so
 * restoring one half without the other would leave the two disagreeing. It is
 * a two-second retype and it is not the thing anyone reaches for undo at 9pm.
 */
export function historySnapshot(board) {
  if (!board) return null;
  return {
    sales: (board.sales || []).map(s => ({ ...s })),
    nominatorIndex: toInt(board.nominatorIndex, 0),
    nomination: board.nomination ? { ...board.nomination } : null,
  };
}

/** Put a snapshot back onto the live board, leaving everything else alone. */
export function applyHistorySnapshot(board, snap) {
  if (!board || !snap) return board;
  return {
    ...board,
    sales: (snap.sales || []).map(s => ({ ...s })),
    nominatorIndex: toInt(snap.nominatorIndex, 0),
    nomination: snap.nomination ? { ...snap.nomination } : null,
  };
}

/**
 * Read the stacks. A corrupt or absent record is EMPTY STACKS, never a throw:
 * the board renders from its own key and must not care what state this one is
 * in. Both arrays are clamped to the depth on the way in, so a hand-edited or
 * older record cannot arrive over-long.
 */
export function readLiveBoardHistory(leagueName) {
  const empty = { undo: [], redo: [] };
  try {
    const raw = JSON.parse(localStorage.getItem(liveBoardHistoryKey(leagueName)));
    if (!raw || typeof raw !== 'object') return empty;
    const clean = (list) => (Array.isArray(list) ? list : [])
      .map(historySnapshot)
      .filter(Boolean)
      .slice(-HISTORY_DEPTH);
    return { undo: clean(raw.undo), redo: clean(raw.redo) };
  } catch {
    return empty;
  }
}

/**
 * 🔴 THIS ONE DOES NOT SWALLOW, AND THAT IS DELIBERATE — it is the only write
 * in this file that does not. Its caller is `commitBoard`, which has already
 * written the BOARD by the time it gets here, and the decision "a quota error
 * on the history key costs the history and nothing else" belongs there, next
 * to the board write it must not disturb. Swallowing here as well would put a
 * second guard in front of the one that matters and hide it from its own test.
 */
export function writeLiveBoardHistory(leagueName, history) {
  localStorage.setItem(liveBoardHistoryKey(leagueName), JSON.stringify({
    undo: (history?.undo || []).slice(-HISTORY_DEPTH),
    redo: (history?.redo || []).slice(-HISTORY_DEPTH),
  }));
  return true;
}

/**
 * The sale that `after` lost from the END of `before`'s list, or null.
 *
 * ⚠️ A COMPARISON, NOT AN INFERENCE. It answers only when the two lists differ
 * by exactly one trailing record — anything else (a delete from the middle, an
 * edit, a nomination, a multi-step jump) returns null and the caller says
 * "undid" without naming anything. Used for one flash message and nothing that
 * touches state.
 */
export function droppedTailSale(before, after) {
  const a = before?.sales || [];
  const b = after?.sales || [];
  if (a.length !== b.length + 1) return null;
  for (let i = 0; i < b.length; i++) {
    if (a[i]?.name !== b[i]?.name || a[i]?.team !== b[i]?.team || a[i]?.price !== b[i]?.price) return null;
  }
  return a[a.length - 1] || null;
}

/** Drop a league's history. Swallows, like clearLiveBoard: it is a cleanup. */
export function clearLiveBoardHistory(leagueName) {
  try {
    localStorage.removeItem(liveBoardHistoryKey(leagueName));
    return true;
  } catch {
    return false;
  }
}

/**
 * How many picks are already entered on this league's board, without building
 * one. Used at app start to decide whether an auction is in progress: a
 * refresh at pick 47 should come back to the board, not to the landing page.
 */
export function liveBoardProgress(leagueName) {
  const b = readLiveBoard(leagueName);
  return b ? b.sales.length : 0;
}

/**
 * Boards with picks in them, stored under a DIFFERENT league than the active
 * one. Read-only: this finds them and reports them, and switches nothing.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE RECOVERY PATH IS LEAGUE-SCOPED, AND THAT IS INVISIBLE FROM THE CHAIR.
 * Every key here is `prefix + leagueName`, so opening the Live Board with the
 * wrong league active renders a blank setup form — while a board with five
 * picks sits in storage one name away. From the operator's seat mid-draft,
 * "my board is gone" and "my board is under a different league name" look
 * identical, and the screen suggests the first by inviting you to start a new
 * one. Ben has two near-identically-named boards with picks in them right now;
 * this is not hypothetical.
 *
 * 🔴 IT MUST STAY SILENT FOR SOMEBODY WHO HAS NEVER MADE A BOARD. No stored
 * boards, or none with a sale in them, returns an empty array and the setup
 * screen is exactly what it has always been. A first-time user must not be told
 * about a recovery path that has nothing to recover.
 *
 * 🔴 AND IT NEVER SWITCHES ANYTHING. Auto-switching the active league would
 * change settings, pricing and tier overrides underneath somebody who opened a
 * page — see the note on loadLeagueFromList. The caller offers; the human
 * decides.
 * ══════════════════════════════════════════════════════════════════════
 *
 * @returns [{ league, leagueName, picks, lastTs }] — `league` is the KEY
 *          suffix, which is what has to become active to open it; `leagueName`
 *          is what the board calls itself, for display. They are normally the
 *          same and are kept apart because a renamed league would make them
 *          differ, and switching on the display name would silently fail.
 *          Most recently touched first.
 */
export function otherBoardsInProgress(activeLeagueName) {
  const active = activeLeagueName || 'default';

  // ⚠️ ENUMERATE FIRST, IN ITS OWN try. The only thing that can fail here is
  // storage itself — private mode, a locked-down browser — and that answer is
  // "say nothing at all".
  const keys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // The teams key does not collide: `auctionArchitect_liveBoardTeams_`
      // diverges from `auctionArchitect_liveBoard_` at the character after
      // "liveBoard", so startsWith is already false.
      if (key && key.startsWith(LIVE_BOARD_KEY_PREFIX)) keys.push(key);
    }
  } catch {
    return [];
  }

  const out = [];
  for (const key of keys) {
    const league = key.slice(LIVE_BOARD_KEY_PREFIX.length);
    if (league === active) continue;
    // ══════════════════════════════════════════════════════════════════
    // 🔴 ONE try PER ENTRY, AND THE SHAPE IS THE POINT.
    //
    // This was one try around the whole loop, which meant ANY throw on ANY
    // entry returned [] — so a single unreadable board silently hid every
    // OTHER board, on the screen whose entire job is to tell you a board
    // exists. Worse: it looks identical to "you have nothing", which is the
    // exact ambiguity this function was written to remove.
    //
    // A MUTATION TEST FOUND IT, and only by accident: deleting the
    // sales-length check made `sales[length - 1].ts` throw on an empty board,
    // the outer catch swallowed it, and the suite stayed green for entirely
    // the wrong reason. That is what a catch too far from its throw buys.
    //
    // Per entry, a bad record costs exactly itself.
    // ══════════════════════════════════════════════════════════════════
    try {
      const board = sanitizeLiveBoard(JSON.parse(localStorage.getItem(key)));
      if (!board || !board.sales.length) continue;
      const last = board.sales[board.sales.length - 1];
      out.push({
        league,
        leagueName: board.leagueName || league,
        picks: board.sales.length,
        lastTs: Number(last && last.ts) || 0,
      });
    } catch { /* this one board is unreadable; the others are not */ }
  }
  return out.sort((a, b) => (b.lastTs - a.lastTs) || (b.picks - a.picks));
}

// ── Team names, remembered across seasons ───────────────────────────
// "Ben's league has stable names year to year — offer last year's." That means
// the names must OUTLIVE the board they were typed into: Start Over wipes the
// sales, and next August the board from this year is long gone, but retyping
// eleven names while people wait is exactly the friction this replaces.
//
// Hence a separate key that clearLiveBoard does not touch.
export const LIVE_TEAMS_KEY_PREFIX = 'auctionArchitect_liveBoardTeams_';

export function liveTeamsKey(leagueName) {
  return LIVE_TEAMS_KEY_PREFIX + (leagueName || 'default');
}

export function readSavedTeamNames(leagueName) {
  try {
    const raw = JSON.parse(localStorage.getItem(liveTeamsKey(leagueName)));
    if (!Array.isArray(raw)) return null;
    const names = raw.filter(n => typeof n === 'string' && n.trim()).map(n => n.trim());
    return names.length ? names : null;
  } catch {
    return null;
  }
}

export function writeSavedTeamNames(leagueName, names) {
  try {
    localStorage.setItem(liveTeamsKey(leagueName), JSON.stringify(names));
    return true;
  } catch {
    return false;
  }
}

/**
 * The names to prefill the setup screen with, best source first:
 *
 *   1. the names typed into this league's last Live Board
 *   2. the opponent nicknames already set for this league in the mock draft
 *      (read from its localStorage key by shape — NOT by importing the hook)
 *   3. the stock placeholder names
 *
 * Always returns exactly `teamCount` entries.
 */
export function suggestedTeamNames(leagueName, teamCount) {
  const out = [];
  const saved = readSavedTeamNames(leagueName);
  if (saved) out.push(...saved);
  if (out.length < teamCount) {
    try {
      const cfg = JSON.parse(localStorage.getItem('auctionArchitect_teamConfigs_' + (leagueName || 'default')));
      if (cfg && typeof cfg === 'object') {
        for (const seat of TEAM_NAMES) {
          const custom = cfg[seat]?.customName;
          if (typeof custom === 'string' && custom.trim() && !out.includes(custom.trim())) out.push(custom.trim());
        }
      }
    } catch { /* no configs for this league — fall through to placeholders */ }
  }
  for (let i = 0; out.length < teamCount; i++) out.push(TEAM_NAMES[i % TEAM_NAMES.length]);
  return out.slice(0, teamCount);
}

// ── Player resolution ───────────────────────────────────────────────
// He is typing while an auctioneer talks and eleven people wait on him, so the
// ranking is tuned for the way people actually say names out loud: last name
// first, and the best player wins ties. "chase" is Ja'Marr, not Chase Brown.

const INDEX = UNIQUE_PLAYERS.map(p => {
  const key = normName(p.name);
  const parts = key.split(' ').filter(Boolean);
  const rank = getAdpRank(p.name);
  return {
    player: p,
    key,
    first: parts[0] || '',
    last: parts.length > 1 ? parts[parts.length - 1] : (parts[0] || ''),
    parts,
    // Off-board players sort last rather than being hidden — a real auction
    // sells players our board has never heard of, and refusing to record one
    // would be the worst possible moment to be strict.
    rank: Number.isFinite(rank) && rank > 0 ? rank : 99999,
  };
});

// Lower is better. Ranked, not scored, so the order is inspectable.
const MATCH_EXACT = 0;      // "bijan robinson"
const MATCH_TWO_TOKEN = 1;  // "j chase" -> first-name prefix + last-name prefix
const MATCH_LAST_EXACT = 2; // "harris"  -> IS the last name
const MATCH_LAST = 3;       // "harris"  -> starts a last name ("Harrison")
const MATCH_FULL = 4;       // "jamarr"  -> full name prefix
const MATCH_TOKEN = 5;      // any other token prefix (first names, middles)
const MATCH_CONTAINS = 6;   // last resort

function matchTier(entry, q, qParts) {
  if (entry.key === q) return MATCH_EXACT;
  if (qParts.length >= 2) {
    const [qf, ql] = [qParts[0], qParts[qParts.length - 1]];
    if (entry.first.startsWith(qf) && entry.last.startsWith(ql)) return MATCH_TWO_TOKEN;
  }
  // A COMPLETE last name outranks one that merely starts a longer name, even
  // when the longer name is the better player: "harris" is Tre Harris, not
  // Marvin Harrison. Without this the finished word loses to a prefix and the
  // only way out is to type the first name too.
  if (entry.last === q) return MATCH_LAST_EXACT;
  if (entry.last.startsWith(q)) return MATCH_LAST;
  if (entry.key.startsWith(q)) return MATCH_FULL;
  if (entry.parts.some(t => t.startsWith(q))) return MATCH_TOKEN;
  if (entry.key.includes(q)) return MATCH_CONTAINS;
  return null;
}

/**
 * Ranked matches for a typed query.
 *
 * @param exclude Set of already-sold player names. Commit is what removes a
 *                player from the board, so a sold player must not come back as
 *                a suggestion — re-entering him is the duplicate-entry bug.
 */
export function searchPlayers(query, { limit = 6, exclude = null } = {}) {
  const q = normName(query || '');
  if (q.length < 2) return [];
  const qParts = q.split(' ').filter(Boolean);
  const hits = [];
  for (const e of INDEX) {
    if (exclude && exclude.has(e.player.name)) continue;
    const tier = matchTier(e, q, qParts);
    if (tier === null) continue;
    hits.push({ e, tier });
  }
  hits.sort((a, b) => (a.tier - b.tier) || (a.e.rank - b.e.rank) || a.e.key.localeCompare(b.e.key));
  return hits.slice(0, limit).map(h => h.e.player);
}

/** The single best match, or null. What Tab accepts. */
export function resolvePlayer(query, opts) {
  return searchPlayers(query, { ...opts, limit: 1 })[0] || null;
}

/** The sale record for a resolved player, with the board rank frozen in. */
export function saleFromPlayer(player, price, teamIndex, now) {
  return {
    name: player.name,
    pos: player.pos,
    posRank: getEffectivePosRank(player.name, player.posRank) || null,
    price: Math.max(0, Math.round(price)),
    team: teamIndex,
    ts: now,
  };
}

// ── Team resolution ─────────────────────────────────────────────────
// First letters resolve. Ambiguity returns -1 rather than guessing: winning a
// player for the wrong team is silent and corrupts every derived number.

/**
 * Matches in tiers, best first: exact, then starts-with, then a word inside
 * the name starts with it, then a bare substring.
 *
 * 🔴 THE TIERS ARE THE POINT, not presentation. A flat list makes "Ch"
 * ambiguous between Chris and Ra-CH-el, so typing it refuses to commit and the
 * user has to keep going — at a real auction, with those two exact names in
 * Ben's league. A name that STARTS with what you typed beats one that merely
 * contains it, which is what "first letters resolve" means.
 */
export function teamMatchTiers(query, teamNames) {
  const q = normName(query || '');
  const exact = [], prefix = [], wordStart = [], contains = [];
  teamNames.forEach((n, index) => {
    const k = normName(n);
    const row = { name: n, index };
    if (k === q) exact.push(row);
    else if (k.startsWith(q)) prefix.push(row);
    else if (k.split(' ').some(t => t.startsWith(q))) wordStart.push(row);
    else if (k.includes(q)) contains.push(row);
  });
  return [exact, prefix, wordStart, contains];
}

export function searchTeams(query, teamNames) {
  if (!normName(query || '')) return teamNames.map((n, i) => ({ name: n, index: i }));
  return teamMatchTiers(query, teamNames).flat();
}

/**
 * The rows resolution is actually DECIDED BY: the best non-empty tier, or [].
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THIS IS WHAT THE ENTRY ROW NAMES WHEN IT REFUSES, and it exists so there
 * is ONE answer rather than two that agree today. `searchTeams` is the wrong
 * list to explain a refusal with: it flattens every tier, so it would name
 * Rachel as a reason "Ch" is ambiguous when Chris beat her outright and the row
 * would have committed. The candidates are only ever the tier that decided.
 *
 * [] means nothing matched at all; one row means it resolved; two or more is
 * the genuine tie `resolveTeam` refuses on.
 * ══════════════════════════════════════════════════════════════════════
 */
export function teamMatchCandidates(query, teamNames) {
  if (!normName(query || '')) return [];
  for (const tier of teamMatchTiers(query, teamNames)) {
    if (tier.length) return tier;
  }
  return [];
}

/**
 * Index of the single unambiguous match, or -1.
 *
 * Resolution happens at the BEST non-empty tier: one hit there wins outright,
 * two or more there is genuinely ambiguous and returns -1. Awarding a player to
 * the wrong team is silent and corrupts every derived number, so a real tie
 * never guesses.
 *
 * ⚠️ UNCHANGED IN BEHAVIOUR — it is now phrased through `teamMatchCandidates`
 * so the gate and the explanation of the gate cannot drift apart. The tier walk
 * that used to be inline is that function; "one hit at the deciding tier" is
 * still exactly the rule, and liveBoard.test.js's existing tier cases (exact,
 * prefix, word-start, contains, empty, tie) pin the equivalence.
 */
export function resolveTeam(query, teamNames) {
  const candidates = teamMatchCandidates(query, teamNames);
  return candidates.length === 1 ? candidates[0].index : -1;
}

// ── Derived state ───────────────────────────────────────────────────

/**
 * Everything the roster grid and the entry-time warnings read.
 *
 * `maxBid` is the number every auction sheet has and everyone computes by hand
 * or gets wrong: what a team can actually bid on the next player while still
 * affording $1 for each remaining slot after it.
 */
export function boardTotals(board) {
  const teamNames = board?.teamNames || [];
  const budget = board?.budget || 200;
  const rosterSize = board?.rosterSize || 16;
  const perTeam = teamNames.map(name => ({ name, spent: 0, count: 0, slotsLeft: rosterSize, maxBid: budget }));
  for (const s of board?.sales || []) {
    const t = perTeam[s.team];
    if (!t) continue;
    t.spent += s.price;
    t.count += 1;
  }
  for (const t of perTeam) {
    t.slotsLeft = Math.max(0, rosterSize - t.count);
    t.maxBid = t.slotsLeft > 0 ? Math.max(0, budget - t.spent - (t.slotsLeft - 1)) : 0;
  }
  const totalSlots = teamNames.length * rosterSize;
  const filled = (board?.sales || []).length;
  // What the room has actually paid, in total. A FACT — the sum of the sale
  // prices already printed on the roster cards — as distinct from roomTemp's
  // tally, which is an opinion about what those players were worth.
  const spent = perTeam.reduce((t, x) => t + x.spent, 0);
  return { perTeam, totalSlots, filled, openSlots: Math.max(0, totalSlots - filled), spent, budget, rosterSize };
}

// ── Who still has a seat at the table ───────────────────────────────
/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 A FULL TEAM IS NOT A TURN. Real auctions skip teams that have finished
 * their roster — they cannot nominate and they cannot bid — and the board's
 * whose-turn prediction has to skip them too or it starts naming people who
 * are done, which is exactly when a prediction stops being believed.
 *
 * 🔴 THESE ARE PURE READS OF `board`, AND THE SKIP HAPPENS ON WRITE. Nothing
 * here mutates `nominatorIndex`; the hook calls `nextOpenSeat` when it advances
 * and STORES the answer. `turnSeat` (utils/boardFocus.js) stays a pure read of
 * that stored value — teach it to skip and a deliberate `setNominator(fullSeat)`
 * would light a different card than the menu row that was clicked, which breaks
 * the escape hatch for exactly the seats an operator most needs to name by hand.
 *
 * 🔴 FULLNESS IS DERIVED FROM `boardTotals`, ALWAYS. There are four disagreeing
 * roster-size derivations in this file and its consumers (buildLiveTeams,
 * boardTotals, RosterBoard, useLiveBoard.rosterSizeOf) and they differ at the
 * FALLBACK. Anchoring every "is this team done" question on one of them is what
 * stops the DONE badge and the "no roster spots left" warning from disagreeing
 * on the same card. Unifying all four is post-draft work.
 * ══════════════════════════════════════════════════════════════════════
 */
export function teamIsFull(board, i) {
  const t = boardTotals(board).perTeam[i];
  return !!t && t.slotsLeft <= 0;
}

/**
 * The next seat with roster space, walking forward from `from` and wrapping.
 *
 * ⚠️ RETURNS -1 ONLY WHEN NOBODY ANYWHERE HAS SPACE — `from` INCLUDED, and that
 * is written as its own check rather than left to fall out of the loop. The
 * near-miss it separates: on a board where `from` is the LAST open seat the
 * answer is `from`, not -1, so the walk has to visit `from` and the "nobody"
 * case cannot be "the walk came back where it started".
 */
export function nextOpenSeat(board, from) {
  const n = board?.teamNames?.length || 0;
  if (n <= 0) return -1;
  if (boardIsComplete(board)) return -1;
  const start = ((Number(from) || 0) % n + n) % n;
  // `step <= n` so the last seat visited is `start` itself.
  for (let step = 1; step <= n; step++) {
    const seat = (start + step) % n;
    if (!teamIsFull(board, seat)) return seat;
  }
  return -1;
}

/** The mirror of nextOpenSeat, for walking a cancelled nomination back. */
export function prevOpenSeat(board, from) {
  const n = board?.teamNames?.length || 0;
  if (n <= 0) return -1;
  if (boardIsComplete(board)) return -1;
  const start = ((Number(from) || 0) % n + n) % n;
  for (let step = 1; step <= n; step++) {
    const seat = ((start - step) % n + n) % n;
    if (!teamIsFull(board, seat)) return seat;
  }
  return -1;
}

/**
 * Every seat full — the draft is over.
 *
 * 🔴 A PROPERTY OF THE SALES LIST, NOT OF `nominatorIndex`. Keeping it separate
 * is what stops the end state from tangling with the override rule: an operator
 * may point the prediction anywhere at any time, and none of that can make a
 * finished draft look unfinished or an unfinished one look done.
 */
export function boardIsComplete(board) {
  const teamNames = board?.teamNames || [];
  if (teamNames.length === 0) return false;
  return boardTotals(board).perTeam.every(t => t.slotsLeft <= 0);
}

/** Names already sold — the exclude set for the player field. */
export function soldNames(board) {
  return new Set((board?.sales || []).map(s => s.name));
}

// ── Rendering the board with the components that already exist ──────
// TeamGrid, ViewAllRostersModal, TierViewer and fillRosterSlots ALL consume the
// mock draft's roster shape:
//
//     [{ player: { name, pos, team, posRank }, price }]
//
// NOT the sale record. They read `pick.player.name`, `pick.player.pos` and
// `pick.player.team` (bye weeks) directly — handing them raw sales renders a
// roster of blank rows with undefined positions. saleToPick is the adapter, and
// it is the reason those components can be reused at all.

const _byName = new Map();
for (const p of UNIQUE_PLAYERS) if (!_byName.has(p.name)) _byName.set(p.name, p);

export function saleToPick(sale) {
  const canon = _byName.get(sale.name);
  return {
    // A free-text sale (a name our board has never heard of) still has to
    // render, so it gets a synthetic player rather than being dropped. `team`
    // is null, which every consumer already treats as "no bye week".
    player: canon || { name: sale.name, pos: sale.pos, team: null, posRank: sale.posRank || null },
    price: sale.price,
  };
}

/** The slot definitions this board rosters against. */
export function liveRosterSlots(board) {
  return buildRosterSlots(board?.positions || {});
}

/**
 * The team array TeamGrid / ViewAllRostersModal render.
 *
 * `budget` is what is LEFT — that is what those components mean by it
 * (TeamGrid derives its spend bar as startingBudget − team.budget).
 *
 * 🔴 `profile` is null and must stay null. It is an AI ARCHETYPE; these seats
 * are real people. Every consumer optional-chains it, and inventing one would
 * print a made-up label on a real person's team on a screen eleven people are
 * watching.
 */
export function buildLiveTeams(board) {
  const rosterSize = board?.rosterSize || rosterSizeOfPositions(board?.positions);
  return (board?.teamNames || []).map((name, i) => {
    const roster = (board.sales || []).filter(s => s.team === i).map(saleToPick);
    const spent = roster.reduce((t, p) => t + p.price, 0);
    const emptySlots = Math.max(0, rosterSize - roster.length);
    return {
      name,
      roster,
      isUser: i === board.userTeam,
      budget: board.budget - spent,             // REMAINING, per the consumers' contract
      spent,
      emptySlots,
      // Matches TeamGrid's own recomputation exactly (it derives this itself
      // rather than reading the field): with one slot left there is nothing to
      // hold back, so the whole remaining budget is biddable.
      //
      // 🔴 …BUT WITH NO SLOTS LEFT IT IS $0, NOT "the whole remaining budget".
      // The `<= 1` collapse printed a finished team's entire leftover money as
      // what it can bid, on the operator's cards AND on /watch — the one number
      // on this board a spectator is entitled to act on, and it was wrong for
      // every team from the moment it finished. RosterBoard:217 carried the
      // identical collapse; both were fixed together.
      maxBid: emptySlots <= 0 ? 0
        : emptySlots <= 1 ? board.budget - spent
          : board.budget - spent - (emptySlots - 1),
      profile: null,
      teamIndex: i,
    };
  });
}

// ── Positional need across the room ─────────────────────────────────
// Whose budget is locked against which position. Free from the sales list, and
// invisible to everyone at a real auction.

const NEED_POSITIONS = ['QB', 'RB', 'WR', 'TE', 'DEF', 'K'];

/**
 * Per team, how many STARTER slots at each position are still empty.
 * Bench and flex are excluded: a bench slot is not a need, and a flex slot is
 * not a need at any one position.
 */
export function teamNeeds(board) {
  const slots = liveRosterSlots(board);
  const starters = slots.filter(s => NEED_POSITIONS.includes(s.pos));
  return buildLiveTeams(board).map(t => {
    const filled = fillRosterSlots(t.roster, slots);
    const need = {};
    for (const s of starters) if (!filled[s.id]) need[s.pos] = (need[s.pos] || 0) + 1;
    return { name: t.name, teamIndex: t.teamIndex, need };
  });
}

/** League-wide roll-up: how many teams still need 1+, and 2+, at each position. */
export function roomNeeds(board) {
  const all = teamNeeds(board);
  return NEED_POSITIONS.map(pos => ({
    pos,
    teamsNeedingOne: all.filter(t => (t.need[pos] || 0) >= 1).length,
    teamsNeedingTwo: all.filter(t => (t.need[pos] || 0) >= 2).length,
  })).filter(r => r.teamsNeedingOne > 0);
}

// ── The tier cliff ──────────────────────────────────────────────────

/**
 * Per position: the best remaining player, the next best, the drop between
 * them, and HOW MANY ARE LEFT IN THE BEST PLAYER'S TIER.
 *
 * The tier count is the part a value list cannot do, and it answers the
 * complaint the r/ff thread agreed on most: being the last person to take
 * someone in the last tier.
 *
 * OBSERVATIONAL. It reports what is on the board. It never says to bid.
 *
 * @param dynamicPrices the canonical user-facing price Map, keyed `name-pos`.
 */
export function tierCliff(board, dynamicPrices, positions = ['QB', 'RB', 'WR', 'TE']) {
  const gone = soldNames(board);
  const priceOf = (p) => dynamicPrices?.get(`${p.name}-${p.pos}`) ?? null;
  return positions.map(pos => {
    const avail = UNIQUE_PLAYERS
      .filter(p => p.pos === pos && !gone.has(p.name))
      .map(p => ({ player: p, price: priceOf(p), tier: tierForPlayer(p)?.tier ?? null }))
      .filter(r => r.price != null)
      .sort((a, b) => b.price - a.price);
    if (!avail.length) return { pos, best: null, next: null, dropPct: null, tier: null, tierLeft: 0 };
    const [best, next] = avail;
    const tier = best.tier;
    return {
      pos,
      best: { name: best.player.name, price: best.price },
      next: next ? { name: next.player.name, price: next.price } : null,
      dropPct: next && best.price > 0 ? ((best.price - next.price) / best.price) * 100 : null,
      tier,
      tierLeft: tier == null ? 0 : avail.filter(r => r.tier === tier).length,
    };
  });
}

// ── CSV export ──────────────────────────────────────────────────────
// `player,pos,price,team` for the finished board. Trivial, and the on-ramp for
// same-season auction data: a user who finishes a real auction holding this
// file is one click from supplying exactly what the analysis line is blocked
// on. Also the manual fallback when the share link is unavailable.

// RFC-4180 quoting. Team names are typed by hand and WILL contain commas
// ("Smith, D") and apostrophes; a naive join silently shifts every later column
// on that row, which corrupts the file in a way nobody notices until it is
// parsed months later.
function csvCell(v) {
  const str = v == null ? '' : String(v);
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

export function boardToCsv(board) {
  const rows = [['player', 'pos', 'price', 'team']];
  for (const s of board?.sales || []) {
    rows.push([s.name, s.pos, s.price, board.teamNames[s.team] ?? '']);
  }
  return rows.map(r => r.map(csvCell).join(',')).join('\r\n');
}

/**
 * Entry-time warnings. These DESCRIBE, they do not block: the auctioneer is
 * the source of truth, and a board that refuses a real sale because our roster
 * math disagrees is useless at the one moment it matters.
 */
export function saleWarnings(board, sale) {
  const out = [];
  if (!sale) return out;
  const totals = boardTotals(board);
  const t = totals.perTeam[sale.team];
  if (!t) return out;
  if (t.slotsLeft <= 0) out.push(`${t.name} has no roster spots left`);
  else if (sale.price > t.maxBid) {
    out.push(`${t.name} can only afford $${t.maxBid} with ${t.slotsLeft} spots to fill`);
  }
  if (soldNames(board).has(sale.name)) out.push(`${sale.name} is already on the board`);
  return out;
}
